import { LNGS_NAMESPACE_CALL_LOGS } from '@ingenico/util-i18n-config';
import { useBusinessServiceRequest, useSetCurrentPageEntityLabel } from '@ingenico/react-section-utils';
import { toString } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import type { TFunction } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import type { DeviceCallLogDetail } from '../../model/callLogs.model';
import { dashString } from '../../utils/dashString.util';

export interface UseDeviceCallLogPageOutput {
  readonly data?: DeviceCallLogDetail | null;
  readonly isError: boolean;
  readonly isLoading: boolean;
  readonly t: TFunction<[string]>;
}
export function useDeviceCallLogPage(): UseDeviceCallLogPageOutput {
  const { t, i18n } = useTranslation(Object.values(LNGS_NAMESPACE_CALL_LOGS));
  const { logicalDeviceId, callId } = useParams<{ logicalDeviceId: string; callId: string }>();
  const setCurrentPageEntityLabel = useSetCurrentPageEntityLabel();

  const deviceCallDetailsRequest = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, _isRefetch) =>
        deviceManagementService.getCallLogsDetails({
          logicalDeviceId: toString(logicalDeviceId),
          callId: toString(callId),
        }),
      [logicalDeviceId, callId],
    ),
  );

  // Load device details to get the device name for breadcrumb
  const logicalDeviceDetailsRequest = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        deviceManagementService.getLogicalDeviceDetailsBff(toString(logicalDeviceId), { isRefetch }),
      [logicalDeviceId],
    ),
  );

  // Set device name in breadcrumb when device data is loaded
  useEffect(() => {
    if (logicalDeviceDetailsRequest.data?.name) {
      setCurrentPageEntityLabel(logicalDeviceDetailsRequest.data.name);
    }
  }, [logicalDeviceDetailsRequest.data?.name, setCurrentPageEntityLabel]);

  const {
    data: responseData,
    isError,
    isLoading,
  } = useMemo(() => deviceCallDetailsRequest, [deviceCallDetailsRequest]);

  const data = useMemo<typeof responseData>(
    () =>
      responseData
        ? {
            ...responseData,
            hardwareDetails: {
              ...responseData.hardwareDetails,
              batteryStatus: i18n.exists(`values.batteryStatus.${responseData?.hardwareDetails.batteryStatus}`, {
                ns: [LNGS_NAMESPACE_CALL_LOGS.DEFAULT],
              })
                ? t(`values.batteryStatus.${responseData?.hardwareDetails.batteryStatus}`)
                : dashString(),
            },
          }
        : null,
    [i18n, responseData, t],
  );

  return {
    data,
    isError,
    isLoading,
    t,
  };
}
